import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import useFetchObjects from "../../../api/useFetchObjects";
import { useAuth } from "../../../context";

const pieColors = ["#4CAF50", "#FF9800", "#90A4AE"];

const StatCard = ({ title, value, subtitle }) => (
  <Card variant="outlined" sx={{ height: "100%", borderRadius: 2 }}>
    <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
        {title}
      </Typography>
      <Typography variant="h5" fontWeight="bold" sx={{ mt: 0.5, lineHeight: 1.1 }}>
        {value}
      </Typography>
      {subtitle ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          {subtitle}
        </Typography>
      ) : null}
    </CardContent>
  </Card>
);

const ReceptionIndex = () => {
  const { t } = useTranslation();
  const periodOptions = [
    { value: "daily", label: t("reception.today", { defaultValue: "Today" }) },
    { value: "weekly", label: t("reception.thisWeek", { defaultValue: "This Week" }) },
    { value: "monthly", label: t("reception.thisMonth", { defaultValue: "This Month" }) },
    { value: "yearly", label: t("reception.thisYear", { defaultValue: "This Year" }) },
    { value: "all", label: t("reception.allTime", { defaultValue: "All Time" }) },
    { value: "custom", label: t("reception.custom", { defaultValue: "Custom" }) },
  ];
  const auth = useAuth();
  const token = auth?.user?.token;
  const [period, setPeriod] = useState("daily");
  const [doctorId, setDoctorId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const reportQuery = useMemo(() => {
    const params = new URLSearchParams();
    params.set("type", period);
    if (doctorId) {
      params.set("doctor_id", doctorId);
    }
    if (period === "custom" && startDate && endDate) {
      params.set("start_date", startDate);
      params.set("end_date", endDate);
    }
    return `reception-reports/operations/?${params.toString()}`;
  }, [period, doctorId, startDate, endDate]);

  const { data: dashboard = {}, isLoading, isError, error } = useFetchObjects(
    ["reception-operations-dashboard", period, doctorId, startDate, endDate],
    reportQuery,
    token
  );

  const { data: doctorsResponse = {} } = useFetchObjects(
    ["reception-operations-doctors"],
    "users/?doctor=true&page_size=500",
    token
  );

  const doctors = Array.isArray(doctorsResponse?.results)
    ? doctorsResponse.results
    : Array.isArray(doctorsResponse)
      ? doctorsResponse
      : [];

  const summary = dashboard?.summary || {};
  const byDoctor = Array.isArray(dashboard?.by_doctor) ? dashboard.by_doctor : [];
  const recentTickets = Array.isArray(dashboard?.recent_tickets) ? dashboard.recent_tickets : [];
  const recentAppointments = Array.isArray(dashboard?.recent_appointments)
    ? dashboard.recent_appointments
    : [];

  const genderData = [
    { name: t("reception.male", { defaultValue: "Male" }), value: summary.male_patients || 0 },
    { name: t("reception.female", { defaultValue: "Female" }), value: summary.female_patients || 0 },
    { name: t("reception.other", { defaultValue: "Other" }), value: summary.unknown_gender_patients || 0 },
  ].filter((item) => item.value > 0);

  const doctorChartData = byDoctor.slice(0, 8).map((row) => ({
    name: row.doctor_name || t("reception.unknown", { defaultValue: "Unknown" }),
    appointments: row.appointment_count || 0,
    patients: row.patient_count || 0,
  }));

  const formatDate = (value) => {
    if (!value) return "-";
    try {
      return format(new Date(value), "dd MMM yyyy");
    } catch {
      return value;
    }
  };

  return (
    <Box sx={{ width: "100%", p: 3, backgroundColor: "#f5f7fb" }}>
      <Stack spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            {t("reception.operationsDashboard", { defaultValue: "Operations Dashboard" })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("reception.operationsDashboardDesc", {
              defaultValue: "Simple operational metrics for reception only.",
            })}
          </Typography>
        </Box>

        <Paper sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("reception.period", { defaultValue: "Period" })}</InputLabel>
                <Select
                  label={t("reception.period", { defaultValue: "Period" })}
                  value={period}
                  onChange={(event) => setPeriod(event.target.value)}
                >
                  {periodOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t("reception.doctor", { defaultValue: "Doctor" })}</InputLabel>
                <Select
                  label={t("reception.doctor", { defaultValue: "Doctor" })}
                  value={doctorId}
                  onChange={(event) => setDoctorId(event.target.value)}
                >
                  <MenuItem value="">{t("reception.allDoctors", { defaultValue: "All Doctors" })}</MenuItem>
                  {doctors.map((doctor) => (
                    <MenuItem key={doctor.id} value={doctor.id}>
                      {`${doctor.first_name || ""} ${doctor.last_name || ""}`.trim() ||
                        doctor.username ||
                        `${t("reception.doctor", { defaultValue: "Doctor" })} ${doctor.id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label={t("reception.startDate", { defaultValue: "Start Date" })}
                InputLabelProps={{ shrink: true }}
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
                disabled={period !== "custom"}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label={t("reception.endDate", { defaultValue: "End Date" })}
                InputLabelProps={{ shrink: true }}
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
                disabled={period !== "custom"}
              />
            </Grid>
          </Grid>
        </Paper>
      </Stack>

      {isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error?.message || t("reception.unableToLoadDashboard", { defaultValue: "Unable to load dashboard." })}
        </Alert>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {isLoading
          ? Array.from({ length: 8 }).map((_, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Skeleton variant="rounded" height={96} />
              </Grid>
            ))
          : [
              { title: t("reception.newPatients", { defaultValue: "New Patients" }), value: summary.new_patients || 0 },
              { title: t("reception.uniquePatients", { defaultValue: "Unique Patients" }), value: summary.unique_opd_patients || 0 },
              { title: t("reception.newOPDPatients", { defaultValue: "New OPD Patients" }), value: summary.new_opd_patients || 0 },
              { title: t("reception.returningOPDPatients", { defaultValue: "Returning OPD Patients" }), value: summary.returning_opd_patients || 0 },
              { title: t("reception.tickets", { defaultValue: "Tickets" }), value: summary.total_tickets || 0 },
              { title: t("reception.OPD", { defaultValue: "OPDs" }), value: summary.total_opds || 0 },
              { title: t("reception.male", { defaultValue: "Male" }), value: summary.male_patients || 0 },
              { title: t("reception.female", { defaultValue: "Female" }), value: summary.female_patients || 0 },
            ].map((item) => (
              <Grid item xs={12} sm={6} md={3} key={item.title}>
                <StatCard title={item.title} value={item.value} />
              </Grid>
            ))}
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              {t("reception.doctorOPDLoad", { defaultValue: "Doctor OPD Load" })}
            </Typography>
            {doctorChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={doctorChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="appointments" fill="#1976d2" name={t("reception.appointments", { defaultValue: "Appointments" })} />
                  <Bar dataKey="patients" fill="#4caf50" name={t("reception.uniquePatients", { defaultValue: "Unique Patients" })} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="text.secondary">
                {t("reception.noDoctorDataForSelectedRange", { defaultValue: "No doctor data for the selected range." })}
              </Typography>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              {t("reception.patientGender", { defaultValue: "Patient Gender" })}
            </Typography>
            {genderData.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={genderData} dataKey="value" nameKey="name" outerRadius={90} label>
                    {genderData.map((entry, index) => (
                      <Cell key={entry.name} fill={pieColors[index % pieColors.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="text.secondary">
                {t("reception.noGenderDataForSelectedRange", { defaultValue: "No gender data in the selected range." })}
              </Typography>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              {t("reception.ticketStatus", { defaultValue: "Ticket Status" })}
            </Typography>
            <Stack spacing={1.5}>
              <StatCard title={t("reception.activeTickets", { defaultValue: "Active Tickets" })} value={summary.active_tickets || 0} />
              <StatCard title={t("reception.completedTickets", { defaultValue: "Completed Tickets" })} value={summary.completed_tickets || 0} />
              <StatCard title={t("reception.cancelledTickets", { defaultValue: "Cancelled Tickets" })} value={summary.cancelled_tickets || 0} />
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              {t("reception.recentTickets", { defaultValue: "Recent Tickets" })}
            </Typography>
            <Stack spacing={1.25}>
              {recentTickets.slice(0, 8).map((ticket) => (
                <Paper key={ticket.id} variant="outlined" sx={{ p: 1.5 }}>
                  <Typography fontWeight="bold">{ticket.ticket_number}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {ticket.patient_name || t("reception.unknownPatient", { defaultValue: "Unknown patient" })} • {formatDate(ticket.visit_date)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    {ticket.department || t("reception.noDepartment", { defaultValue: "No department" })} • {ticket.status || t("reception.unknown", { defaultValue: "Unknown" })}
                  </Typography>
                </Paper>
              ))}
              {recentTickets.length === 0 ? (
                <Typography color="text.secondary">
                  {t("reception.noRecentTicketsFound", { defaultValue: "No recent tickets found." })}
                </Typography>
              ) : null}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              {t("reception.recentOPDs", { defaultValue: "Recent OPDs" })}
            </Typography>
            <Grid container spacing={2}>
              {recentAppointments.slice(0, 6).map((appointment) => (
                <Grid item xs={12} md={6} lg={4} key={appointment.id}>
                  <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
                    <Typography fontWeight="bold">
                      {appointment.patient_name || t("reception.unknownPatient", { defaultValue: "Unknown patient" })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {appointment.ticket_number || t("reception.noTicket", { defaultValue: "No ticket" })} • {formatDate(appointment.visit_date)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      {appointment.doctor_name || t("reception.unknownDoctor", { defaultValue: "Unknown doctor" })} • {appointment.status || t("reception.unknown", { defaultValue: "Unknown" })}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
              {recentAppointments.length === 0 ? (
                <Grid item xs={12}>
                  <Typography color="text.secondary">
                    {t("reception.noRecentOPDsFound", { defaultValue: "No recent OPDs found." })}
                  </Typography>
                </Grid>
              ) : null}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ReceptionIndex;

